import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import type { MessageDetail, StreamEvent } from '@/api/types';
import { emitSessionSignal } from '@/api/sessionSignals';
import {
  clearArrivals,
  clearStreamGap,
  recordAccountStatus,
  recordArrivals,
} from './arrivals';
import { effectsForEvent, patchesForEvent, type RealtimeEffect } from './reducer';
import { createRealtimeCachePatchSink } from './sink';
import { conversationSignalsFor, signalConversations } from './conversationSignals';
import { whenTaskQueueIdle } from '@/features/tasks/writeQueue';
import { invalidateConversations } from '@/features/conversation/cache';
import { closeMessageNotices } from './messageNotice';
import { messageIdForUid } from './events';

/**
 * `tasks_changed`: refetch the mailbox's task reads — the mounted ones now,
 * the rest when next shown. Cheap: a list is a few rows out of Postgres.
 *
 * Held back while this tab still has task writes going out. The frame may be
 * the echo of the first of them, and refetching now would replace the list
 * with the server's copy from before the rest arrived, dropping their rows
 * until the next refetch put them back. One refetch per client and mailbox,
 * once the queue is empty, answers every frame that came in meanwhile.
 */
const taskRefreshers = new WeakMap<QueryClient, Map<string, () => void>>();
function refreshTasks(client: QueryClient, accountId: string): void {
  let perAccount = taskRefreshers.get(client);
  if (!perAccount) {
    perAccount = new Map();
    taskRefreshers.set(client, perAccount);
  }
  let refresh = perAccount.get(accountId);
  if (!refresh) {
    refresh = () => {
      void client.invalidateQueries({ queryKey: queryKeys.account(accountId).tasksScope() });
    };
    perAccount.set(accountId, refresh);
  }
  whenTaskQueueIdle(accountId, refresh);
}

/**
 * The impure half: takes what `reducer.ts` computed and does it to a
 * `QueryClient`.
 *
 * Patches go through the shared sink, so a pushed change and a locally-made one
 * write through identical code. Effects are the documented exception — they
 * drop and refetch cache entries, which a patch is never allowed to do — and
 * each one below says why it is allowed to.
 */

/**
 * A conversation list (ADR 0077), per account or merged. Kept apart from the
 * message lists below: those are made good by refetching, these by `sync`
 * (see `conversationSignals.ts`), which is the point of them on a folder of
 * 20,000 messages.
 */
function isConversationList(key: QueryKey): boolean {
  return (key[0] === 'account' && key[2] === 'conversations') ||
    (key[0] === 'unified' && key[1] === 'conversations');
}

/** Filters live in the 4th slot of a message-list key: `['account', id, 'messages', {…}]`. */
function listKeyFolderId(key: QueryKey): string | undefined {
  const filters = key[3];
  if (typeof filters !== 'object' || filters === null) return undefined;
  const folderId = (filters as { folderId?: unknown }).folderId;
  return typeof folderId === 'string' ? folderId : undefined;
}

/** The lanes of a merged list's key: `['unified', 'conversations', { accounts: 'a,b' }]`. */
function unifiedKeyAccounts(key: QueryKey): string[] {
  const filters = key[2];
  if (typeof filters !== 'object' || filters === null) return [];
  const accounts = (filters as { accounts?: unknown }).accounts;
  return typeof accounts === 'string' ? accounts.split(',') : [];
}

/**
 * A frame changed one folder (an arrival, another client's flags or
 * expunge): every conversation list of it that is not on screen is marked
 * stale — never refetched here — so it is fetched when next shown. A shown
 * one is left to its `sync` (ADR 0077 §5), which the same frame rings for.
 *
 * "Not on screen" is no ENABLED observer, not no observer: while a search is
 * showing, MailView keeps the folder's conversation list observed but
 * disabled, and a disabled list has no `sync` listening either — so the frame
 * was lost, and clearing the search brought back the old rows inside their
 * stale time (PR #534's journey). Re-enabling a list marked stale refetches.
 *
 * Merged lists too, any this account is a lane of: their key names accounts,
 * not folders, so a frame for another of the account's folders costs one
 * refetch when next shown.
 */
function markHiddenConversationListsStale(
  client: QueryClient,
  accountId: string,
  folderId: string,
): void {
  void client.invalidateQueries({
    queryKey: queryKeys.account(accountId).conversationsScope(),
    refetchType: 'none',
    predicate: (query) => !query.isActive() && listKeyFolderId(query.queryKey) === folderId,
  });
  void client.invalidateQueries({
    queryKey: queryKeys.unifiedConversationsScope(),
    refetchType: 'none',
    predicate: (query) =>
      !query.isActive() && unifiedKeyAccounts(query.queryKey).includes(accountId),
  });
}

/**
 * Drops every cached message list for one folder, and every message detail the
 * cache holds that belongs to it.
 *
 * The details are found by reading each cached `MessageDetail.folderId` — the
 * value the server put there — and never by inspecting the id. After
 * `UIDVALIDITY` changes the ids are not just stale, they are **wrong**: the same
 * string now addresses a different message, so refetching under the old key
 * would replace one message's cache entry with another's. Removing is the only
 * safe move.
 */
function discardFolder(client: QueryClient, accountId: string, folderId: string): void {
  const keys = queryKeys.account(accountId);

  client.removeQueries({
    queryKey: keys.messagesScope(),
    predicate: (query) => listKeyFolderId(query.queryKey) === folderId,
  });

  for (const [key, detail] of client.getQueriesData<MessageDetail>({
    queryKey: keys.messageScope(),
  })) {
    if (detail?.folderId !== folderId) continue;
    const messageId = key[3];
    client.removeQueries({ queryKey: key, exact: true });
    if (typeof messageId === 'string') {
      client.removeQueries({ queryKey: keys.messageRaw(messageId), exact: true });
    }
  }

  // The merged list of ADR 0056 holds this folder's rows too, under ids that
  // now address different messages. There is no folder in its key, so the
  // whole merged list goes: it is one list of several mailboxes' Inboxes and
  // cannot lose one of them selectively.
  client.removeQueries({ queryKey: queryKeys.unifiedMessagesScope() });
  void client.refetchQueries({ queryKey: queryKeys.unifiedMessagesScope(), type: 'active' });

  // Its conversation lists (ADR 0077) too: their rows are made of the same
  // stale ids, and no `sync` can patch its way across a UIDVALIDITY change.
  //
  // A shown list is RESET rather than removed: removing a query an observer
  // still holds leaves the observer on the orphan, so the refetch that
  // follows finds nothing to fetch and the stale rows stay until something
  // happens to re-render the view. `resetQueries` drops the data and
  // refetches the mounted ones in the same step.
  client.removeQueries({
    queryKey: keys.conversationsScope(),
    type: 'inactive',
    predicate: (query) => listKeyFolderId(query.queryKey) === folderId,
  });
  void client.resetQueries({
    queryKey: keys.conversationsScope(),
    predicate: (query) => listKeyFolderId(query.queryKey) === folderId,
  });
  client.removeQueries({ queryKey: queryKeys.unifiedConversationsScope(), type: 'inactive' });
  void client.resetQueries({ queryKey: queryKeys.unifiedConversationsScope() });

  // Those parked ids name different messages now too.
  clearArrivals(accountId, folderId);

  // Refetch only what is actually mounted; nothing is fetched for a folder the
  // user is not looking at.
  void client.refetchQueries({
    queryKey: keys.messagesScope(),
    type: 'active',
    predicate: (query) => listKeyFolderId(query.queryKey) === folderId,
  });
  void client.invalidateQueries({ queryKey: keys.folders() });
}

/**
 * `resync_required`: the stream missed events and cannot say which.
 *
 * Every list for the account that nothing is showing is dropped, and every one
 * that something is showing refetches in place (see `discardThenRefetch`);
 * counts are marked stale. This is the one case where a visible list is allowed
 * to move, and it is correct that it does: the alternative is rendering a list
 * the server has already told us is wrong. It is not allowed to go blank.
 *
 * Message *bodies* are kept. A delivered body is immutable (that is why the
 * detail query uses `STALE_TIMES.immutable`), and a gap in the event stream
 * says nothing about the bytes of a message already fetched — only about which
 * messages exist and what their flags are, both of which live in the lists and
 * counts being dropped here.
 */
function resync(client: QueryClient, accountId: string): void {
  // The server said so and every mounted list is about to refetch, which is
  // exactly what a recorded gap would have asked a view to do. Clearing it
  // here stops the pill offering a refresh that has already happened.
  clearStreamGap();
  if (accountId === '') {
    // No account on the frame means the whole stream fell behind.
    discardThenRefetch(client, ['account']);
    discardThenRefetch(client, queryKeys.unifiedMessagesScope());
    return;
  }
  const keys = queryKeys.account(accountId);
  discardThenRefetch(client, keys.messagesScope());
  // The merged list mixes this account's rows with the others'; a gap in one
  // lane makes the merged order unreliable as a whole.
  discardThenRefetch(client, queryKeys.unifiedMessagesScope());
  // Conversation lists nobody is showing go; the shown ones are brought up to
  // date by their own `sync`, which the same frame rings for.
  client.removeQueries({ queryKey: keys.conversationsScope(), type: 'inactive' });
  client.removeQueries({ queryKey: queryKeys.unifiedConversationsScope(), type: 'inactive' });
  clearArrivals(accountId);
  void client.invalidateQueries({ queryKey: keys.folders() });
}

/**
 * Drops what nobody is looking at, and refetches what somebody is — **in
 * place**.
 *
 * Removing a *mounted* list before refetching it is what made the list "flash
 * black over and over": the view is left with no rows, renders its loading
 * skeleton until the page comes back, and loses its scroll position with the
 * virtualized rows. A folder too large for the watcher to diff answers every
 * mark-as-read with a `resync_required`, so clicking through messages was a
 * blank list per click. The rows on screen stay until the fresh page replaces
 * them; that is a list the server called possibly-stale for a few hundred
 * milliseconds, which is better than no list.
 *
 * A burst of frames is coalesced into the refetch in flight plus ONE trailing
 * refetch. Restarting the fetch per frame (TanStack's `cancelRefetch`) aborted
 * each request mid-flight, and an aborted request costs the backend its IMAP
 * connection — the next one has to dial again.
 */
function discardThenRefetch(client: QueryClient, queryKey: QueryKey): void {
  client.removeQueries({ queryKey, type: 'inactive' });
  refetchInPlace(client, queryKey);
}

const inFlight = new WeakMap<QueryClient, Map<string, { again: boolean }>>();

/**
 * Refetches one list's loaded pages without dropping them, one in flight and
 * one trailing (see above). Exported for the conversation `sync` that answers
 * `reset` (ADR 0077): the server could not patch, so the list is refetched —
 * in place, like any other.
 */
export function refetchInPlace(client: QueryClient, queryKey: QueryKey): void {
  let running = inFlight.get(client);
  if (!running) {
    running = new Map();
    inFlight.set(client, running);
  }
  const id = JSON.stringify(queryKey);
  const current = running.get(id);
  if (current) {
    // A refetch is already out; it may have been answered before this change,
    // so one more follows it — but only one, however many frames arrive.
    current.again = true;
    return;
  }
  const state = { again: false };
  running.set(id, state);
  const table = running;
  void (async () => {
    try {
      do {
        state.again = false;
        // Never a conversation list under a wider prefix (`['account']`):
        // those sync. Asked for by its own key (a `sync` answering `reset`),
        // it is refetched like any other.
        await client.invalidateQueries({
          queryKey,
          refetchType: 'active',
          ...(isConversationList(queryKey)
            ? {}
            : { predicate: (query) => !isConversationList(query.queryKey) }),
        });
      } while (state.again);
    } finally {
      table.delete(id);
    }
  })();
}

export function applyRealtimeEffects(
  client: QueryClient,
  effects: readonly RealtimeEffect[],
): void {
  for (const effect of effects) {
    switch (effect.kind) {
      case 'resync':
        resync(client, effect.accountId);
        break;

      case 'folder-invalidated':
        discardFolder(client, effect.accountId, effect.folderId);
        break;

      case 'folder-list':
        void client.invalidateQueries({ queryKey: queryKeys.account(effect.accountId).folders() });
        break;

      case 'tasks-changed':
        refreshTasks(client, effect.accountId);
        break;

      case 'labels-changed':
        // A few rows out of Postgres: the mounted list refetches now (in
        // place — the sidebar and the chips keep what they have until the
        // answer lands), a hidden one when next shown.
        void client.invalidateQueries({ queryKey: queryKeys.account(effect.accountId).labels() });
        break;

      case 'arrival':
        recordArrivals(effect.accountId, effect.folderId, effect.messageIds);
        // Every cached list of that folder no longer holds everything in it.
        // Marked stale, never refetched here: a list on screen keeps its rows
        // and waits behind the pill (doc 06 §5), and a hidden one is fetched
        // when it is next opened — instead of serving a copy up to 30 s old
        // that is missing the very message the folder count just announced.
        void client.invalidateQueries({
          queryKey: queryKeys.account(effect.accountId).messagesScope(),
          refetchType: 'none',
          predicate: (query) => listKeyFolderId(query.queryKey) === effect.folderId,
        });
        // A hidden conversation list of that folder is stale the same way.
        markHiddenConversationListsStale(client, effect.accountId, effect.folderId);
        break;

      case 'account-status': {
        const status = recordAccountStatus(effect.accountId, effect.status, effect.reason);
        // `reauth_required` is terminal: the backend has stopped retrying and
        // will not resume without a password, so the app must ask for one.
        // Reusing the session layer's own `expired` signal raises the existing
        // `ReauthDialog` over the app — no navigation, no remount, no lost
        // draft — rather than inventing a second password prompt.
        if (status === 'reauth-required') {
          emitSessionSignal({
            type: 'expired',
            code: 'reauth_required',
            status: 401,
          });
        }
        break;
      }
    }
  }
}

/**
 * One event, all the way through: reduce, patch, then effects.
 *
 * Patches first. An `expunge` that removes rows and then invalidates a folder
 * count must not run in the other order, or the count is recomputed from a
 * list that still holds the rows.
 */
export function applyStreamEvent(client: QueryClient, event: StreamEvent): void {
  const sink = createRealtimeCachePatchSink(client);
  sink(patchesForEvent(event));
  applyRealtimeEffects(client, effectsForEvent(event));
  // A flag change or an expunge leaves conversation lists to their `sync`,
  // which a hidden list does not run (an arrival does this in its effect).
  if ((event.kind === 'flags' || event.kind === 'expunge') &&
    event.accountId !== undefined && event.accountId !== '' && event.folderId !== undefined) {
    markHiddenConversationListsStale(client, event.accountId, event.folderId);
  }
  // Last: a list that syncs reads the cache the patches and effects left.
  for (const signal of conversationSignalsFor(event)) signalConversations(client, signal);
  refreshConversationDetails(client, event);
  closeHandledNotices(event);
}

/**
 * Read on another device, or gone: its notification on this one has nothing
 * left to say (ADR 0106). `\Seen` in a flags frame's new set means read.
 */
export function handledMessageIds(event: StreamEvent): string[] {
  if (event.kind === 'expunge') return event.messageIds ?? [];
  if (event.kind !== 'flags') return [];
  const ids: string[] = [];
  for (const change of event.flags ?? []) {
    if (!change.flags.some((flag) => flag.toLowerCase() === '\\seen')) continue;
    const id = messageIdForUid(event, change.uid);
    if (id !== undefined) ids.push(id);
  }
  return ids;
}

function closeHandledNotices(event: StreamEvent): void {
  if (event.accountId === undefined || event.accountId === '') return;
  const ids = handledMessageIds(event);
  if (ids.length > 0) void closeMessageNotices(event.accountId, ids);
}

/**
 * The reading pane's open conversation (ADR 0077) after a frame that can
 * have changed its members: a reply arriving (in any folder — a Sent copy is
 * a member too), an expunge, a gap. Refetched in place. Flag frames are not
 * here: those patch each member's flags exactly.
 */
function refreshConversationDetails(client: QueryClient, event: StreamEvent): void {
  switch (event.kind) {
    case 'new_mail':
    case 'expunge':
    case 'folder_invalidated':
      if (event.accountId === undefined || event.accountId === '') return;
      void invalidateConversations(client, event.accountId);
      return;
    case 'resync_required':
      void invalidateConversations(client, event.accountId ?? '');
      return;
    default:
      return;
  }
}
